/* Friday Hall spark v1 (2026-10-03). Pairs with fw-spark.css. No libraries: this file works with or without
   fw-motion.js (GSAP / Lenis), and everything that moves only runs when <head> set html.fw-m (no reduced motion,
   no Save-Data, no 2G). One rAF-coalesced scroll handler, mouse-only pointer effects, no work while idle.
   Per-feature switches: comment out the matching call in the list at the bottom of this file. */
(() => {
  const html = document.documentElement;
  if (window.fwSpark) return;
  window.fwSpark = { version: 1 };

  const motion = html.classList.contains('fw-m');
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const wrapper = document.querySelector('.page-wrapper');
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  /* Horizon scrolls .page-wrapper (not the window) from 990px up; the same rule fw-motion.js uses. */
  let box = null;
  const measure = () => {
    box = wrapper && getComputedStyle(wrapper).overflowY === 'auto' ? wrapper : null;
  };
  const top = () => (box ? box.scrollTop : window.scrollY);
  const max = () => (box ? box.scrollHeight - box.clientHeight : html.scrollHeight - window.innerHeight);

  /* ---------- Scroll progress hairline, quick-shop strip, hero badge peel (one scroll handler) ---------- */
  function chrome() {
    const bar = document.createElement('div');
    bar.className = 'fw-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.append(bar);

    const qn = $('[data-fw-qn]');
    const sentinel = $('[data-fw-ticker]') || $('.fw-deck');
    const header = $('#header-component');
    const badge = motion ? $('.fw-deck__badge') : null;

    let pending = false;
    const frame = () => {
      pending = false;
      const y = top();
      const m = max();
      bar.style.transform = `scaleX(${m > 0 ? clamp(y / m, 0, 1).toFixed(4) : 0})`;

      if (qn && sentinel) {
        const hb = header ? header.getBoundingClientRect().bottom : 0;
        qn.style.setProperty('--fw-qn-top', `${Math.max(0, Math.round(hb))}px`);
        qn.classList.toggle('is-on', sentinel.getBoundingClientRect().top < hb);
      }
      if (badge) badge.style.setProperty('--p', clamp(y / (window.innerHeight * 0.7), 0, 1).toFixed(3));
    };
    const onScroll = () => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(frame);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    wrapper?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      measure();
      onScroll();
    });
    frame();
    return onScroll;
  }

  /* ---------- Count-up for facts (the final number is already in the HTML; motion only) ---------- */
  function counters() {
    const els = $$('[data-fw-count]');
    if (!els.length || !motion || !('IntersectionObserver' in window)) return;
    const run = (el, end) => {
      const start = performance.now();
      const dur = 1500;
      const tick = (now) => {
        const t = clamp((now - start) / dur, 0, 1);
        const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t); // expo out
        el.textContent = Math.round(end * eased).toLocaleString('en-US');
        if (t < 1) window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          run(entry.target, Number(entry.target.dataset.fwCount));
        }),
      { threshold: 0.6 }
    );
    els.forEach((el) => {
      const end = Number(el.dataset.fwCount);
      if (!Number.isFinite(end)) return;
      // CRO 2026-10-03: a fact already on screen keeps its real number (never flashes 0); only facts that
      // start below the screen are zeroed, and they count up when they scroll in.
      if (el.getBoundingClientRect().top < window.innerHeight) return;
      el.style.display = 'inline-block';
      el.style.minWidth = `${el.textContent.length}ch`; // the final width is reserved, so nothing shifts
      el.textContent = '0';
      io.observe(el);
    });
  }

  /* ---------- Tilt + moving light on photos (mouse only): product cards and [data-fw-tilt] tiles ---------- */
  function tilt() {
    if (!motion || !fine) return;
    const SEL = '.fw-card__media, [data-fw-tilt]';
    let cur = null;
    let raf = 0;
    let px = 0;
    let py = 0;
    const apply = () => {
      raf = 0;
      if (!cur) return;
      const r = cur.getBoundingClientRect();
      const x = (px - r.left) / r.width;
      const y = (py - r.top) / r.height;
      const m = parseFloat(cur.dataset.fwTilt) || 4;
      cur.style.setProperty('--fw-rx', `${((x - 0.5) * 2 * m).toFixed(2)}deg`);
      cur.style.setProperty('--fw-ry', `${((0.5 - y) * 2 * m).toFixed(2)}deg`);
      cur.style.setProperty('--fw-sx', `${(x * 100).toFixed(1)}%`);
      cur.style.setProperty('--fw-sy', `${(y * 100).toFixed(1)}%`);
    };
    const stop = () => {
      if (!cur) return;
      cur.classList.remove('is-tilting');
      cur.style.removeProperty('--fw-rx');
      cur.style.removeProperty('--fw-ry');
      cur = null;
    };
    document.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        const hit = event.target instanceof Element ? event.target.closest(SEL) : null;
        if (hit !== cur) {
          stop();
          cur = hit;
          cur?.classList.add('is-tilting');
        }
        if (!cur) return;
        px = event.clientX;
        py = event.clientY;
        if (!raf) raf = window.requestAnimationFrame(apply);
      },
      { passive: true }
    );
    html.addEventListener('pointerleave', stop);
    window.addEventListener('blur', stop);
    window.addEventListener('scroll', stop, { passive: true });
    wrapper?.addEventListener('scroll', stop, { passive: true });
  }

  /* ---------- Hero: a soft cobalt light follows the cursor ---------- */
  function heroLight() {
    const deck = $('.fw-deck');
    if (!deck || !motion || !fine) return;
    let raf = 0;
    let ev = null;
    const set = () => {
      raf = 0;
      const r = deck.getBoundingClientRect();
      deck.style.setProperty('--fw-mx', `${Math.round(ev.clientX - r.left)}px`);
      deck.style.setProperty('--fw-my', `${Math.round(ev.clientY - r.top)}px`);
    };
    deck.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        ev = event;
        deck.classList.add('is-lit');
        if (!raf) raf = window.requestAnimationFrame(set);
      },
      { passive: true }
    );
    deck.addEventListener('pointerleave', () => deck.classList.remove('is-lit'));
  }

  /* ---------- Footer sign-off: the outlined letters fill cobalt, one by one, as the footer comes into view ---------- */
  function sign() {
    const el = $('[data-fw-sign]');
    if (!el) return;
    if (!motion || !('IntersectionObserver' in window)) {
      el.classList.add('is-in'); // static: filled, no animation
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        el.classList.add('is-in');
        io.disconnect();
      },
      { threshold: 0.35 }
    );
    io.observe(el);
  }

  /* ---------- Card loading skeleton: only cards near the screen whose photo has not arrived yet ---------- */
  function skeleton() {
    if (!motion) return;
    const near = window.innerHeight * 2.5;
    $$('.fw-card__media').forEach((media) => {
      const img = $('.fw-card__img:not(.fw-card__img--alt)', media);
      if (!img || img.complete) return;
      if (media.getBoundingClientRect().top < near) media.classList.add('is-loading');
    });
    const done = (event) => {
      const t = event.target;
      if (t instanceof HTMLImageElement && t.classList.contains('fw-card__img')) {
        t.closest('.fw-card__media')?.classList.remove('is-loading');
      }
    };
    document.addEventListener('load', done, true);
    document.addEventListener('error', done, true);
  }

  function boot() {
    measure();
    const run = (fn) => {
      try {
        fn();
      } catch (error) {
        window.setTimeout(() => {
          throw error; // visible in the console, never breaks the page
        });
      }
    };
    // Per-feature switches: comment out a line to turn that effect off.
    run(chrome);
    run(counters);
    run(tilt);
    run(heroLight);
    run(sign);
    run(skeleton);
  }

  // CRO 2026-10-03: nothing here is needed for first paint, so it waits for load and an idle moment
  // (keeps its layout reads and DOM writes off the critical path). Rollback: replace the three lines below with
  // `if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();`
  const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(boot, { timeout: 1500 }) : window.setTimeout(boot, 1));
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
})();
